import {useCallback, useContext, useMemo, useState} from "react";
import {SecurityContext} from "context/security";
import {CLEARING_SYSTEM_CODE_LIST_URL} from "service/UrlConstant";
import {USABA_CLEARING_SYSTEM_CODE} from "pages/admin/wiretemplate/AddNewWireTemplateStep1";

/**
 * Shared logic for the Clearing System Code combobox used by both the create and
 * edit clearing system screens.
 *
 * It fetches the list of existing clearing system codes and builds the dropdown
 * options. The API populates the list asynchronously, so the options are always
 * seeded with the default USABA code and the currently selected code to guarantee
 * the dropdown is never empty on the first render.
 *
 * @param {string} selectedCode the code currently entered/selected in the form.
 * @returns {{
 *   clearingSystemCodeList: string[],
 *   getClearingSystemCodeList: () => void,
 *   clearingSystemCodeOptions: {label: string, value: string}[]
 * }}
 */
export default function useClearingSystemCodes(selectedCode) {
    const {makeGetRequestNoParams} = useContext(SecurityContext);
    const [clearingSystemCodeList, setClearingSystemCodeList] = useState([]);

    const getClearingSystemCodeList = useCallback(() => {
        const onSuccessResponse = (res) => {
            const data = res.data || [];
            // Normalize to a list of unique, non-empty code strings so the field
            // works whether the API returns strings or objects.
            const codes = data
                .map(item => (typeof item === 'string' ? item : (item && item.clearingSystemCode)))
                .filter(code => !!code);
            setClearingSystemCodeList([...new Set(codes)]);
        }

        const onErrorResponse = (err) => {
            console.log("Error on get clearing system code list: ", err);
        }

        makeGetRequestNoParams(CLEARING_SYSTEM_CODE_LIST_URL, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    // Build the Clearing System Code dropdown options. Seed with the default USABA
    // code and whatever the form currently holds so the dropdown is never empty on
    // the first hit and re-computes automatically once the API data arrives.
    const clearingSystemCodeOptions = useMemo(() => {
        const codes = [
            USABA_CLEARING_SYSTEM_CODE,
            ...clearingSystemCodeList,
            selectedCode
        ].filter(code => !!code && !!code.toString().trim());

        return [...new Set(codes)].map(code => ({label: code, value: code}));
    }, [clearingSystemCodeList, selectedCode])

    return {clearingSystemCodeList, getClearingSystemCodeList, clearingSystemCodeOptions};
}

